import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_864cc152-5d80-4928-acac-3bcce367b776/artifacts/dhju5nz8_Grassroots%20Coach%20Logo%20260329.png";

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const theme = localStorage.getItem('landingTheme') || 'dark';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      // For demo purposes, show the reset token (remove in production)
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      toast.success('Reset instructions sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-container" data-theme={theme}>
        <div className="w-full max-w-md px-4">
          {/* Logo and Title - Outside the card */}
          <div className="text-center mb-8 animate-fadeIn">
            <div className="w-24 h-24 mx-auto mb-4">
              <Link to="/" data-testid="forgot-logo-home-link">
                <img src={LOGO_URL} alt="Logo" className="w-24 h-24 object-contain cursor-pointer hover:opacity-80 transition-opacity" />
              </Link>
            </div>
            <h1 className="text-3xl font-bold text-white font-title tracking-tight">
              Check Your Email
            </h1>
            <p className="text-white/80 mt-2">
              If an account exists for {email}, you'll receive reset instructions.
            </p>
          </div>

          {/* Form Card */}
          <Card className="auth-card border-0 shadow-2xl animate-fadeIn">
            <CardContent className="pt-6 space-y-6">
              {/* Demo: Show reset link - Remove in production */}
              {resetToken && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm text-amber-800 font-medium mb-2">Demo Mode: Reset Link</p>
                  <Link 
                    to={`/reset-password/${resetToken}`}
                    className="text-sm break-all underline"
                  >
                    Click here to reset your password
                  </Link>
                </div>
              )}
              
              <div className="text-center">
                <Link 
                  to="/login" 
                  className="inline-flex items-center gap-2 font-semibold"
                >
                  <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                  Back to Sign In
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Back to landing page */}
          <div className="text-center mt-6 animate-fadeIn">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium"
              data-testid="forgot-submitted-back-to-landing"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              Back to main page
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container" data-theme={theme}>
      <div className="w-full max-w-md px-4">
        {/* Logo and Title - Outside the card */}
        <div className="text-center mb-8 animate-fadeIn">
          <div className="w-24 h-24 mx-auto mb-4">
            <Link to="/" data-testid="forgot-logo-home-link">
              <img src={LOGO_URL} alt="Logo" className="w-24 h-24 object-contain cursor-pointer hover:opacity-80 transition-opacity" />
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-white font-title tracking-tight">
            Forgot Password?
          </h1>
          <p className="text-white/80 mt-2">
            Enter your email and we'll send you reset instructions
          </p>
        </div>

        {/* Form Card */}
        <Card className="auth-card border-0 shadow-2xl animate-fadeIn">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" strokeWidth={1.5} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl text-white font-semibold shadow-lg btn-auth btn-transition"
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 font-semibold"
              >
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Back to landing page */}
        <div className="text-center mt-6 animate-fadeIn">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium"
            data-testid="forgot-back-to-landing"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back to main page
          </Link>
        </div>
      </div>
    </div>
  );
}
